import { PostRole, PostTextAction, SocialNetwork } from 'models/socialCampaign'

import { textLimitOf } from './postEditor'

/**
 * A post's text as the AI actions see it (ISSUE-3619), the backend's `PostTextParts` (#625): the `body` they rewrite,
 * then the `closing` lines they keep (the essentials line that closes a highlight or a relaunch, and the hashtag lines
 * at the very end). `body + closing` is the text. Keep in step with the backend: an action it would refuse is not offered.
 */
export interface PostTextParts {
  body: string
  closing: string
  /** Every hashtag the text holds, in order */
  hashtags: string[]
}

/** The backend's `GeneratedPostTextChecker.MAX_HASHTAGS_PER_POST` */
export const MAX_HASHTAGS_PER_POST = 30

/** A blank line, and every blank line after it */
const SEPARATOR = /\n[ \t\u00A0]*\n\s*/g
const HASHTAG = /#[\p{L}\p{N}_]+/gu
const HASHTAG_LINE = /^#[\p{L}\p{N}_]+(?:\s+#[\p{L}\p{N}_]+)*$/u
const CLOSED_BY_ESSENTIALS = [PostRole.HIGHLIGHT, PostRole.RELAUNCH]
/**
 * The posts the backend tells again from the story when they have no body (`SocialCampaignTextService.storyTexts`).
 * A highlight whose story key no longer resolves gets nothing either, which the page cannot tell: it stays offered.
 */
const TOLD_FROM_STORY = [PostRole.OVERVIEW, PostRole.HIGHLIGHT]

const isHashtagLine = (paragraph: string) => HASHTAG_LINE.test(paragraph.trim())

/**
 * Walking back from the end, hashtag-only and blank paragraphs are closing, and so is one more paragraph for a story
 * post that ends on its essentials; the first paragraph always stays in the body.
 */
export const postTextPartsOf = (text: string, role: PostRole): PostTextParts => {
  let cut = text.length
  let essentialsKept = !CLOSED_BY_ESSENTIALS.includes(role)
  for (;;) {
    const head = text.slice(0, cut)
    const separators = Array.from(head.matchAll(SEPARATOR))
    const separator = separators[separators.length - 1]
    if (!separator || separator.index === undefined) break
    const paragraph = head.slice(separator.index + separator[0].length).trim()
    if (paragraph && !isHashtagLine(paragraph)) {
      if (essentialsKept) break
      essentialsKept = true
    }
    cut = separator.index
  }
  return { body: text.slice(0, cut), closing: text.slice(cut), hashtags: Array.from(text.matchAll(HASHTAG), (match) => match[0]) }
}

/**
 * Which actions the backend would take on this text (`SocialCampaignTextService.maxLength`): Regenerate needs a body,
 * or an overview or a highlight (the backend tells it again from the story); Shorter a body of two characters or more;
 * More hashtags fewer than 30 and room under the networks' limit. Each also needs room for the body beside the closing
 * lines.
 */
export const textActionsOf = (text: string, role: PostRole, networks: SocialNetwork[]): Record<PostTextAction, boolean> => {
  const parts = postTextPartsOf(text, role)
  const limit = textLimitOf(networks).limit
  const body = parts.body.trim()
  const bodyRoom = limit - parts.closing.length > 0
  return {
    [PostTextAction.REGENERATE]: (body.length > 0 || TOLD_FROM_STORY.includes(role)) && bodyRoom,
    [PostTextAction.SHORTER]: body.length > 1 && bodyRoom,
    [PostTextAction.MORE_HASHTAGS]: parts.hashtags.length < MAX_HASHTAGS_PER_POST && limit - (parts.body + parts.closing).trimEnd().length - 2 > 0,
  }
}
